import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import auth from "../../services/authService";
import Swal from "sweetalert2";
import { loadRolePermission, loadUserDetails } from "../../app/store/auth";

// Modern SVG Hexagon Cell Component
const HexagonCell = ({ type, category, label, activeCategory }) => {
  const isMain = type === "main";

  let highlightClass = "";
  if (activeCategory !== null) {
    if (activeCategory === category) {
      highlightClass = "is-highlighted";
    } else {
      highlightClass = "is-dimmed";
    }
  }

  const bgFill = isMain ? `url(#grad-${category}-main)` : `url(#grad-${category}-sub)`;

  return (
    <div className={`modern-hex-cell ${isMain ? "is-main" : "is-sub"} cat-${category} ${highlightClass}`}>
      <svg className="hex-svg" viewBox="0 0 117 130">
        <polygon
          className="hex-bg-polygon"
          points="58.5,2 115,34 115,96 58.5,128 2,96 2,34"
          fill={bgFill}
        />
        <polygon
          className='hex-sheen-polygon'
          points='58.5,6 111.5,36 111.5,94 58.5,124 5.5,94 5.5,36'
          fill='url(#hex-sheen)'
        />
        <polygon
          className='hex-inner-border'
          points='58.5,6 111.5,36 111.5,94 58.5,124 5.5,94 5.5,36'
        />
      </svg>
      <div className="modern-hex-content">
        <span className="modern-hex-label">{label}</span>
      </div>
    </div>
  );
};

const OnboardingHexagon = () => {
  const [currentUser, setCurrentUser] = useState("");
  const [user, setUser] = useState("");
  const [rolePermission, setRolePermission] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.getCurrentUser();
      const currentUser = await dispatch(loadUserDetails(user.id));
      if (!currentUser) return;
      setUser(user);
      setCurrentUser(currentUser);

      if (currentUser?.registrationType === "WP") {
        const permission = await dispatch(loadRolePermission(currentUser.roleId));
        if (permission !== "") {
          setRolePermission(permission);
        }
      }
    };

    fetchData();
  }, [dispatch]);

  const handleCategoryClick = (category) => {
    setActiveCategory(prev => prev === category ? null : category);
  };

  const checkMenuPermission = (mid) => {
    if (user.type === "admin") return true;
    const mm = mid.split("_");
    let chk = false;
    if (mm[0] === "m") {
      const mR = rolePermission.filter(
        (x) => x.id === parseInt(mm[1]) && x.isChecked === true
      );
      if (mR.length > 0) {
        chk = true;
      } else {
        rolePermission
          .filter((x) => x.id === parseInt(mm[1]))
          .forEach((menu) => {
            const sMenu = menu.menus.filter((x) => x.isChecked === true);
            if (sMenu.length > 0) {
              chk = true;
            } else {
              menu.menus.forEach((ssmenu) => {
                if (ssmenu?.menus) {
                  const ssMenu = ssmenu.menus.filter(
                    (x) => x.isChecked === true
                  );
                  if (ssMenu.length > 0) {
                    chk = true;
                  }
                }
              });
            }
          });
      }
    }
    if (mm[0] === "sm") {
      rolePermission
        .filter((x) => x.id === parseInt(mm[2]))
        .forEach((menu) => {
          const sMenu = menu.menus.filter(
            (x) => x.id === parseInt(mm[1]) && x.isChecked === true
          );
          if (sMenu.length > 0) {
            chk = true;
          } else {
            menu.menus.forEach((smenu) => {
              if (smenu?.menus) {
                const ssMenu = smenu.menus.filter((x) => x.isChecked === true);
                if (ssMenu.length > 0) {
                  chk = true;
                }
              } else {
                if (smenu?.isChecked) {
                  chk = true;
                }
              }
            });
          }
        });
    }
    if (mm[0] === "ssm") {
      rolePermission
        .filter((x) => x.id === parseInt(mm[3]))
        .forEach((menu) =>
          menu.menus
            .filter((x) => x.id === parseInt(mm[2]))
            .forEach((ssmenu) => {
              if (ssmenu?.menus) {
                const ssMenu = ssmenu.menus.filter(
                  (x) => x.id === parseInt(mm[1]) && x.isChecked === true
                );
                if (ssMenu.length > 0) {
                  chk = true;
                }
              } else {
                if (ssmenu?.isChecked) chk = true;
              }
            })
        );
    }
    return chk;
  };

  const permissionAlert = () => {
    Swal.fire("Your are not authorized", "", "error");
  };

  let loggedUser = { ...user };
  if (
    loggedUser.type !== "admin" &&
    currentUser.roleType === "Complete Application Ownership"
  ) {
    loggedUser.type = "admin";
  }

  return (
    <div id="content">
      {/* Stylesheet Overrides for Modern Rich SVG Hexagon Experience */}
      <style>{`
        #content {
          min-height: 100%;
          width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          flex: 1 1 auto !important;
        }

        #page {
          width: 100% !important;
          box-sizing: border-box !important;
          flex: 1 1 auto !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: flex-start !important;
          background: radial-gradient(circle at 10% 10%, rgba(59, 130, 246, 0.03), transparent 40%),
                      radial-gradient(circle at 90% 90%, rgba(16, 185, 129, 0.03), transparent 40%),
                      linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%) !important;
          padding: 24px 0 40px 0 !important;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 24px;
          box-shadow: 0 18px 55px rgba(15, 23, 42, 0.08),
                      inset 0 0 40px rgba(15, 23, 42, 0.02);
        }

        .wf-hex-legend {
          position: relative;
          width: 100% !important;
          max-width: none !important;
          box-sizing: border-box !important;
          margin: 0 0 14px;
          padding: 16px 20px !important;
          border: 1px solid rgba(255, 255, 255, 0.82);
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.7);
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07),
                      inset 0 1px 0 rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(16px);
        }

        .wf-hex-hero {
          position: relative;
          width: 100%;
          max-width: none;
          margin: 0 0 14px;
          padding: 26px 34px 24px;
          overflow: hidden;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 18px;
          background:
            radial-gradient(circle at 92% 20%, rgba(139, 92, 246, 0.14), transparent 32%),
            linear-gradient(135deg, #ffffff 0%, #edf3ff 100%);
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07),
                      inset 0 1px 0 rgba(255, 255, 255, 0.95);
        }

        .wf-hex-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 26px;
          right: 0px;
          width: auto !important;
          height: 3px;
          border-left: none !important;
          border-radius: 999px;
          background: linear-gradient(90deg, #3b82f6, #10b981, #8b5cf6, #f43f5e, #eab308);
        }

        .wf-hex-hero-kicker {
          margin-bottom: 7px;
          color: #4f46e5;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .wf-hex-page-title {
          margin: 0 0 8px;
          color: #525861;
          font-size: clamp(1rem, 2.6vw, 1.5rem);
          font-weight: 600;
          line-height: 1.18;
          letter-spacing: -0.025em;
        }

        .wf-hex-hero-copy {
          max-width: 780px;
          margin: 0;
          color: #52627a;
          font-size: 0.86rem;
          line-height: 1.55;
        }

        .wf-hex-hero-mark {
          position: absolute;
          top: 50%;
          right: 42px;
          transform: translateY(-50%);
          color: #6366f1;
          font-size: 5rem;
          opacity: 0.08;
          pointer-events: none;
        }

        .wf-hex-legend::before {
          content: '';
          position: absolute;
          top: 0;
          left: 20px;
          right: 20px;
          height: 2px;
          border-radius: 999px;
          background: linear-gradient(90deg, #3b82f6, #10b981, #f59e0b, #8b5cf6, #f43f5e, #eab308);
          opacity: 0.8;
        }

        .wf-hex-legend-title {
          color: #1e293b !important;
          font-size: 0.76rem !important;
          letter-spacing: 0.12em !important;
        }

        .wf-hex-legend-chips {
          flex: 1;
          justify-content: flex-start;
        }

        @media (max-width: 900px) {
          #content { padding: 10px; }
          #page { border-radius: 18px; }
          .wf-hex-legend { margin-bottom: 10px; }
          .wf-hex-hero { padding: 24px 22px 22px; }
          .wf-hex-hero-mark { display: none; }
        }

        #data_wrapper {
          display: flex !important;
          justify-content: center !important;
          align-items: flex-start !important;
          width: 100% !important;
          padding: 0 !important;
          overflow: visible !important;
          height: 600px !important;
        }

        #data {
          position: relative !important;
          left: auto !important;
          margin: 0 auto !important;
          width: 1060px !important;
          height: 600px !important;
          overflow: visible !important;
          transform: translateX(-46px) !important;
        }

        #data .item {
          opacity: 1 !important;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: visible;
        }

        #data .item a,
        #data .item span {
          background: none !important;
          border: none !important;
          padding: 0 !important;
          width: 117px !important;
          height: 130px !important;
          display: block;
          position: relative;
          text-decoration: none !important;
          cursor: pointer;
        }

        .modern-hex-cell {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          transform: scale(0.91); /* Creates a clean spacing gap between hexagons */
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          --hex-accent: #64748b; --hex-subtle: #e2e8f0;
          --hex-selected: #475569;
          isolation: isolate;
        }

        .modern-hex-cell.cat-pre { --hex-accent: #3b82f6; --hex-subtle: #dbeafe; --hex-selected: #1d4ed8; }
        .modern-hex-cell.cat-create { --hex-accent: #10b981; --hex-subtle: #d1fae5; --hex-selected: #047857; }
        .modern-hex-cell.cat-build { --hex-accent: #f59e0b; --hex-subtle: #fef3c7; --hex-selected: #b45309; }
        .modern-hex-cell.cat-test { --hex-accent: #8b5cf6; --hex-subtle: #ede9fe; --hex-selected: #6d28d9; }
        .modern-hex-cell.cat-deploy { --hex-accent: #f43f5e; --hex-subtle: #ffe4e6; --hex-selected: #be123c; }
        .modern-hex-cell.cat-monitor { --hex-accent: #eab308; --hex-subtle: #fef9c3; --hex-selected: #a16207; }

        .hex-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
        }

        .modern-hex-cell:hover {
          transform: translateY(-6px) scale(1.02); /* Pops out slightly on hover */
          filter: drop-shadow(0 16px 28px rgba(15, 23, 42, 0.15));
          z-index: 50;
        }

        .modern-hex-cell .hex-bg-polygon {
          stroke-width: 1.5px;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .modern-hex-cell .hex-bg-polygon {
          fill: var(--hex-accent) !important;
        }

        .modern-hex-cell.is-sub .hex-bg-polygon {
          fill: var(--hex-subtle) !important;
        }

        .modern-hex-cell.is-highlighted.is-sub .hex-bg-polygon {
          fill: var(--hex-accent) !important;
        }

        .hex-sheen-polygon {
          pointer-events: none;
          opacity: 0.62;
          transition: opacity 0.35s ease;
        }

        .hex-inner-border {
          fill: none;
          stroke: rgba(255, 255, 255, 0.38);
          stroke-width: 0.7px;
          pointer-events: none;
          transition: stroke 0.35s ease, stroke-width 0.35s ease;
        }

        .modern-hex-cell.is-sub .hex-inner-border {
          stroke: rgba(255, 255, 255, 0.8);
        }

        .modern-hex-cell:hover .hex-sheen-polygon { opacity: 0.92; }

        .modern-hex-cell:hover .hex-inner-border {
          stroke: rgba(255, 255, 255, 0.92);
          stroke-width: 1px;
        }

        /* Solid White stroke and rich shadows for Main category headers */
        .modern-hex-cell.is-main .hex-bg-polygon {
          fill: #3b82f6 !important;
          stroke: #ffffff;
          stroke-width: 2px;
          filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.12));
        }

        .modern-hex-cell.is-main:hover .hex-bg-polygon {
          fill: var(--hex-accent) !important;
          stroke-width: 2.5px;
          stroke: #ffffff;
        }

        /* Category Color Specific Strokes */
        .modern-hex-cell.is-sub.cat-pre .hex-bg-polygon {
          stroke: rgba(59, 130, 246, 0.3);
        }
        .modern-hex-cell.is-sub.cat-pre:hover .hex-bg-polygon {
          stroke: rgba(59, 130, 246, 0.85);
          fill: url(#grad-pre-sub-hover);
        }

        .modern-hex-cell.is-sub.cat-create .hex-bg-polygon {
          stroke: rgba(16, 185, 129, 0.3);
        }
        .modern-hex-cell.is-sub.cat-create:hover .hex-bg-polygon {
          stroke: rgba(16, 185, 129, 0.85);
          fill: url(#grad-create-sub-hover);
        }

        .modern-hex-cell.is-sub.cat-build .hex-bg-polygon {
          stroke: rgba(234, 88, 12, 0.3);
        }
        .modern-hex-cell.is-sub.cat-build:hover .hex-bg-polygon {
          stroke: rgba(234, 88, 12, 0.85);
          fill: url(#grad-build-sub-hover);
        }

        .modern-hex-cell.is-sub.cat-test .hex-bg-polygon {
          stroke: rgba(147, 51, 234, 0.3);
        }
        .modern-hex-cell.is-sub.cat-test:hover .hex-bg-polygon {
          stroke: rgba(147, 51, 234, 0.85);
          fill: url(#grad-test-sub-hover);
        }

        .modern-hex-cell.is-sub.cat-deploy .hex-bg-polygon {
          stroke: rgba(225, 29, 72, 0.3);
        }
        .modern-hex-cell.is-sub.cat-deploy:hover .hex-bg-polygon {
          stroke: rgba(225, 29, 72, 0.85);
          fill: url(#grad-deploy-sub-hover);
        }

        .modern-hex-cell.is-sub.cat-monitor .hex-bg-polygon {
          stroke: rgba(217, 119, 6, 0.3);
        }
        .modern-hex-cell.is-sub.cat-monitor:hover .hex-bg-polygon {
          stroke: rgba(217, 119, 6, 0.85);
          fill: url(#grad-monitor-sub-hover);
        }

        /* Highlighting and Dimming States */
        /* Permission-denied tiles are intentionally muted and non-interactive. */
        .item a[href*='#access-denied'] {
          cursor: not-allowed;
        }

        .item a[href*='#access-denied'] .modern-hex-cell {
          opacity: 0.62;
          filter: grayscale(1) saturate(0);
          cursor: not-allowed;
        }

        .item a[href*='#access-denied'] .hex-bg-polygon {
          fill: #cbd5e1 !important;
          stroke: #94a3b8 !important;
        }

        .item a[href*='#access-denied'] .hex-sheen-polygon {
          opacity: 0.18 !important;
        }

        .item a[href*='#access-denied'] .hex-inner-border {
          stroke: #f8fafc !important;
        }

        .item a[href*='#access-denied'] .modern-hex-icon,
        .item a[href*='#access-denied'] .modern-hex-label {
          color: #64748b !important;
        }

        .item a[href*='#access-denied'] .modern-hex-cell:hover {
          transform: none;
          filter: grayscale(1) saturate(0) !important;
        }

        .modern-hex-cell.is-dimmed {
          opacity: 0.18 !important;
          filter: grayscale(60%) blur(0.5px);
          transform: scale(0.85);
        }

        .modern-hex-cell.is-highlighted {
          opacity: 1 !important;
          transform: translateY(-4px) scale(0.96);
        }

        .modern-hex-cell.is-highlighted.is-main {
          transform: translateY(-6px) scale(1.02);
        }

        .modern-hex-cell.is-highlighted.is-main .hex-bg-polygon {
          fill: var(--hex-selected) !important;
          stroke: #ffffff !important;
          stroke-width: 3px !important;
        }

        /* Glow effects for highlighted categories */
        .modern-hex-cell.is-highlighted.cat-pre {
          filter: drop-shadow(0 0 15px rgba(59, 130, 246, 0.6)) !important;
        }
        .modern-hex-cell.is-highlighted.cat-create {
          filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.6)) !important;
        }
        .modern-hex-cell.is-highlighted.cat-build {
          filter: drop-shadow(0 0 15px rgba(234, 88, 12, 0.6)) !important;
        }
        .modern-hex-cell.is-highlighted.cat-test {
          filter: drop-shadow(0 0 15px rgba(147, 51, 234, 0.6)) !important;
        }
        .modern-hex-cell.is-highlighted.cat-deploy {
          filter: drop-shadow(0 0 15px rgba(225, 29, 72, 0.6)) !important;
        }
        .modern-hex-cell.is-highlighted.cat-monitor {
          filter: drop-shadow(0 0 15px rgba(217, 119, 6, 0.6)) !important;
        }
        
        /* Highlight specific borders for sub-cells */
        .modern-hex-cell.is-highlighted.is-sub.cat-pre .hex-bg-polygon {
          stroke: rgba(59, 130, 246, 0.9) !important;
          stroke-width: 2px !important;
        }
        .modern-hex-cell.is-highlighted.is-sub.cat-create .hex-bg-polygon {
          stroke: rgba(16, 185, 129, 0.9) !important;
          stroke-width: 2px !important;
        }
        .modern-hex-cell.is-highlighted.is-sub.cat-build .hex-bg-polygon {
          stroke: rgba(234, 88, 12, 0.9) !important;
          stroke-width: 2px !important;
        }
        .modern-hex-cell.is-highlighted.is-sub.cat-test .hex-bg-polygon {
          stroke: rgba(147, 51, 234, 0.9) !important;
          stroke-width: 2px !important;
        }
        .modern-hex-cell.is-highlighted.is-sub.cat-deploy .hex-bg-polygon {
          stroke: rgba(225, 29, 72, 0.9) !important;
          stroke-width: 2px !important;
        }
        .modern-hex-cell.is-highlighted.is-sub.cat-monitor .hex-bg-polygon {
          stroke: rgba(217, 119, 6, 0.9) !important;
          stroke-width: 2px !important;
        }

        /* Soft fade on grid hover to focus on the active node (when no active category is selected) */
        #data:not(.has-active-selection):hover .item:not(:hover) {
          opacity: 1 !important;
        }

        /* Overlay Text & Icon Layout */
        .modern-hex-content {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 6px;
          text-align: center !important;
          padding: 0 12px;
          box-sizing: border-box;
          z-index: 5;
          pointer-events: none;
          user-select: none;
        }

        .modern-hex-icon {
          display: grid !important;
          place-items: center;
          flex: 0 0 auto;
          width: 30px;
          height: 30px;
          margin: 0 !important;
          text-align: center !important;
          float: none !important;
          line-height: 1 !important;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        #data .item .modern-hex-label {
          display: flex !important;
          align-items: center;
          justify-content: center;
          text-align: center !important;
          width: 104px !important;
          height: auto !important;
          max-width: 100%;
          margin: 0 !important;
          padding: 0 !important;
          position: static !important;
          box-sizing: border-box;
          white-space: normal !important;
          overflow-wrap: normal;
          word-break: normal;
          hyphens: none;
          text-wrap: balance;
          cursor: inherit;
        }

        /* Main Headers Styling */
        .modern-hex-cell.is-main .modern-hex-content {
          padding: 0 10px;
          gap: 3px;
        }

        .modern-hex-cell.is-main .modern-hex-icon {
          width: 34px;
          height: 34px;
          font-size: 1.35rem;
          color: #ffffff;
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.24);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22),
                      0 5px 12px rgba(15, 23, 42, 0.12);
        }

        .modern-hex-cell.is-main .modern-hex-label {
          color: #ffffff;
          font-size: 0.65rem;
          font-weight: 800;
          letter-spacing: 0.025em;
          line-height: 1.15;
          text-transform: uppercase;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        /* Sub-cells styling */
        .modern-hex-cell.is-sub .modern-hex-content {
          padding: 0 10px;
          gap: 3px;
        }

        .modern-hex-cell.is-sub .modern-hex-icon {
          font-size: 1rem;
          color: #ffffff !important;
          background: rgba(255, 255, 255, 0.16);
          border: 1px solid color-mix(in srgb, var(--hex-accent) 18%, transparent);
          box-shadow: 0 5px 12px color-mix(in srgb, var(--hex-accent) 10%, transparent);
          opacity: 0.95;
        }

        .modern-hex-cell.is-sub.cat-pre .modern-hex-icon { color: #2563eb; }
        .modern-hex-cell.is-sub.cat-create .modern-hex-icon { color: #16a34a; }
        .modern-hex-cell.is-sub.cat-build .modern-hex-icon { color: #ea580c; }
        .modern-hex-cell.is-sub.cat-test .modern-hex-icon { color: #9333ea; }
        .modern-hex-cell.is-sub.cat-deploy .modern-hex-icon { color: #e11d48; }
        .modern-hex-cell.is-sub.cat-monitor .modern-hex-icon { color: #d97706; }

        .modern-hex-cell.is-sub .modern-hex-label {
          font-size: 0.58rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.1;
          letter-spacing: 0.01em;
          text-transform: uppercase;
        }

        /* Hover Transitions */
        .modern-hex-cell.is-sub:hover .modern-hex-icon {
          transform: scale(1.08) translateY(-1px);
          opacity: 1;
          background: color-mix(in srgb, var(--hex-accent) 17%, transparent);
          box-shadow: 0 7px 16px color-mix(in srgb, var(--hex-accent) 20%, transparent);
        }

        .modern-hex-cell.is-sub:hover .modern-hex-label {
          color: #ffffff;
        }

        .modern-hex-cell.is-sub .modern-hex-label,
        .modern-hex-cell.is-sub:hover .modern-hex-label {
          color: #334155 !important;
        }

        .modern-hex-cell.is-highlighted.is-sub .modern-hex-label,
        .modern-hex-cell.is-highlighted.is-sub:hover .modern-hex-label {
          color: #ffffff !important;
        }

        @media (prefers-reduced-motion: reduce) {
          #data .item,
          .modern-hex-cell,
          .modern-hex-cell * {
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      {/* Global Shared SVG Gradients Definition */}
      <svg style={{ position: "absolute", width: 0, height: 0 }} aria-hidden="true">
        <defs>
          <linearGradient id='hex-sheen' x1='18%' y1='8%' x2='82%' y2='92%'>
            <stop offset='0%' stopColor='rgba(255, 255, 255, 0.32)' />
            <stop offset='42%' stopColor='rgba(255, 255, 255, 0.05)' />
            <stop offset='100%' stopColor='rgba(255, 255, 255, 0)' />
          </linearGradient>
          {/* Main Cells Gradients */}
          <linearGradient id="grad-pre-main" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#1d4ed8" />
          </linearGradient>
          <linearGradient id="grad-create-main" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#047857" />
          </linearGradient>
          <linearGradient id="grad-build-main" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#d97706" />
          </linearGradient>
          <linearGradient id="grad-test-main" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#6d28d9" />
          </linearGradient>
          <linearGradient id="grad-deploy-main" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f43f5e" />
            <stop offset="100%" stopColor="#be123c" />
          </linearGradient>
          <linearGradient id="grad-monitor-main" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#eab308" />
            <stop offset="100%" stopColor="#ca8a04" />
          </linearGradient>

          {/* Sub-cells Glassmorphic Gradients */}
          <linearGradient id="grad-pre-sub" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(59, 130, 246, 0.05)" />
            <stop offset="100%" stopColor="rgba(37, 99, 235, 0.12)" />
          </linearGradient>
          <linearGradient id="grad-create-sub" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(16, 185, 129, 0.05)" />
            <stop offset="100%" stopColor="rgba(4, 120, 87, 0.12)" />
          </linearGradient>
          <linearGradient id="grad-build-sub" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(245, 158, 11, 0.05)" />
            <stop offset="100%" stopColor="rgba(217, 119, 6, 0.12)" />
          </linearGradient>
          <linearGradient id="grad-test-sub" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(139, 92, 246, 0.05)" />
            <stop offset="100%" stopColor="rgba(109, 40, 217, 0.12)" />
          </linearGradient>
          <linearGradient id="grad-deploy-sub" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(244, 63, 94, 0.05)" />
            <stop offset="100%" stopColor="rgba(190, 18, 60, 0.12)" />
          </linearGradient>
          <linearGradient id="grad-monitor-sub" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(250, 204, 21, 0.05)" />
            <stop offset="100%" stopColor="rgba(202, 138, 4, 0.12)" />
          </linearGradient>

          {/* Hover Glassmorphic Gradients */}
          <linearGradient id="grad-pre-sub-hover" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(59, 130, 246, 0.15)" />
            <stop offset="100%" stopColor="rgba(37, 99, 235, 0.28)" />
          </linearGradient>
          <linearGradient id="grad-create-sub-hover" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(16, 185, 129, 0.15)" />
            <stop offset="100%" stopColor="rgba(4, 120, 87, 0.28)" />
          </linearGradient>
          <linearGradient id="grad-build-sub-hover" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(245, 158, 11, 0.15)" />
            <stop offset="100%" stopColor="rgba(217, 119, 6, 0.28)" />
          </linearGradient>
          <linearGradient id="grad-test-sub-hover" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(139, 92, 246, 0.15)" />
            <stop offset="100%" stopColor="rgba(109, 40, 217, 0.28)" />
          </linearGradient>
          <linearGradient id="grad-deploy-sub-hover" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(244, 63, 94, 0.15)" />
            <stop offset="100%" stopColor="rgba(190, 18, 60, 0.28)" />
          </linearGradient>
          <linearGradient id="grad-monitor-sub-hover" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(250, 204, 21, 0.15)" />
            <stop offset="100%" stopColor="rgba(202, 138, 4, 0.28)" />
          </linearGradient>
        </defs>
      </svg>

      <div
        id="page"
        onClick={(e) => {
          const anchor = e.target.closest("a");
          if (anchor) {
            const href = anchor.getAttribute("href");
            if (href === "#access-denied") {
              e.preventDefault();
              alert("Access Denied");
            } else if (href === "#") {
              e.preventDefault();
            }
          } else {
            // Clicked empty space: reset highlight
            setActiveCategory(null);
          }
        }}
      >
        <div id="data_wrapper">
          <div id={`data`} className={activeCategory ? "has-active-selection" : ""}>
            {/* COLUMN 1: System Administration (pre) */}
            <div>
              <div className="item main r1 c2 even_row pre" rel="pre">
                <Link to="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('pre'); }}>
                  <HexagonCell type="main" category="pre" label="System Admin" icon="fa fa-cogs" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item pre r2 c2">
                <Link
                  to={`${checkMenuPermission("ssm_1_1_1")
                    ? "/WorkflowUser"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="pre" label="Main Users" icon="fa fa-user-circle-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item pre r2 c3">
                <Link
                  to={`${checkMenuPermission("ssm_3_2_1")
                    ? "/ListCondidate/partner"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="pre" label="Candidates" icon="fa fa-graduation-cap" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item pre even_row r3 c2">
                <Link
                  to={`${checkMenuPermission("ssm_1_2_1")
                    ? "/ListSubcontractor/partner"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="pre" label="Subcontractors" icon="fa fa-handshake-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item pre r4 c3">
                <Link
                  to={`${checkMenuPermission("ssm_2_2_1")
                    ? "/Recruiter/partner"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="pre" label="Recruiters" icon="fa fa-users" activeCategory={activeCategory} />
                </Link>
              </div>
            </div>

            {/* COLUMN 2: Onboarding & Contracts (create) */}
            <div>
              <div className="item main r1 c3 even_row create" rel="create">
                <Link to="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('create'); }}>
                  <HexagonCell type="main" category="create" label="Onboarding" icon="fa fa-file-text" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item create r2 c4">
                <Link
                  to={`${checkMenuPermission("ssm_3_3_2")
                    ? "/ListCondidate/application"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="create" label="Candidates" icon="fa fa-graduation-cap" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item create even_row r3 c3">
                <Link
                  to={`${checkMenuPermission("ssm_1_1_2")
                    ? "/ListSubcontractor/application"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="create" label="Subcontractors" icon="fa fa-handshake-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item create r4 c4">
                <Link
                  to={`${checkMenuPermission("ssm_2_2_2")
                    ? "/Recruiter/application"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="create" label="Recruiters" icon="fa fa-users" activeCategory={activeCategory} />
                </Link>
              </div>
            </div>

            {/* COLUMN 3: Invoices (build) */}
            <div>
              <div className="item main r1 c4 even_row build" rel="build">
                <Link to="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('build'); }}>
                  <HexagonCell type="main" category="build" label="Invoices" icon="fa fa-money" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item build r2 c5">
                <Link
                  to={`${checkMenuPermission("ssm_1_1_3")
                    ? "/ListCondidate/timelog"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="build" label="Time Log Management" icon="fa fa-clock-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item build even_row r3 c4">
                <Link
                  to={`${checkMenuPermission("ssm_2_2_3")
                    ? "/ListCondidate/invoice"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="build" label="Invoices and Payments" icon="fa fa-money" activeCategory={activeCategory} />
                </Link>
              </div>
            </div>

            {/* COLUMN 4: Analytics (test) */}
            <div>
              <div className="item main r1 c5 even_row test" rel="test">
                <Link to="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('test'); }}>
                  <HexagonCell type="main" category="test" label="Analytics" icon="fa fa-bar-chart" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item test r2 c6">
                <Link
                  to={`${checkMenuPermission("ssm_1_1_4")
                    ? "/Dashboard"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="test" label="Dashboard" icon="fa fa-tachometer" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item test even_row r3 c5">
                <Link
                  to={`${checkMenuPermission("ssm_2_2_4")
                    ? "/ApplicationReport"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="test" label="Entity Data" icon="fa fa-database" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item test even_row r3 c6">
                <Link
                  to={`${checkMenuPermission("ssm_3_3_4")
                    ? "/CustomReport/report"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="test" label="Business Reports" icon="fa fa-bar-chart" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item test r4 c5">
                <Link
                  to={`${checkMenuPermission("ssm_5_5_4")
                    ? "/CustomReport/compliance"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="test" label="Compliance Reports/Docs" icon="fa fa-check-square-o" activeCategory={activeCategory} />
                </Link>
              </div>
            </div>

            {/* COLUMN 5: Security (deploy) */}
            <div>
              <div className="item main r1 c6 even_row deploy" rel="deploy">
                <Link to="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('deploy'); }}>
                  <HexagonCell type="main" category="deploy" label="Security" icon="fa fa-shield" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item deploy r2 c7">
                <Link
                  to={`${checkMenuPermission("ssm_1_1_5")
                    ? "/MonitoringLogs"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="deploy" label="Monitoring Logs" icon="fa fa-shield" activeCategory={activeCategory} />
                </Link>
              </div>
            </div>

            {/* COLUMN 6: Communication (monitor) */}
            <div>
              <div className="item main r1 c7 even_row monitor" rel="monitor">
                <Link to="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('monitor'); }}>
                  <HexagonCell type="main" category="monitor" label="Communications" icon="fa fa-comments" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item monitor r2 c8">
                <Link
                  to={`${checkMenuPermission("ssm_3_1_6")
                    ? "/ApplicantEmails/mail"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="monitor" label="Candidates" icon="fa fa-envelope" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item monitor even_row r3 c7">
                <Link
                  to={`${checkMenuPermission("ssm_1_1_6")
                    ? "/PartnerEmails/mail"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="monitor" label="Subcontractors" icon="fa fa-envelope-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item monitor r4 c7">
                <Link
                  to={`${checkMenuPermission("ssm_2_1_6")
                    ? "/AgencyEmails/mail"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="monitor" label="Recruiters" icon="fa fa-envelope-open-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item monitor even_row r3 c8">
                <Link
                  to={`${checkMenuPermission("ssm_3_2_6")
                    ? "/ApplicantEmails/text"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="monitor" label="Candidates" icon="fa fa-comments-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item monitor r4 c8">
                <Link
                  to={`${checkMenuPermission("ssm_1_2_6")
                    ? "/PartnerEmails/text"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="monitor" label="Subcontractors" icon="fa fa-mobile" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item monitor r4 c9">
                <Link
                  to={`${checkMenuPermission("ssm_2_2_6")
                    ? "/AgencyEmails/text"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="monitor" label="Recruiters" icon="fa fa-comment-o" activeCategory={activeCategory} />
                </Link>
              </div>
              <div className="item monitor r4 c6">
                <Link
                  to={`${checkMenuPermission("ssm_5_5_6")
                    ? "/SurveyFeedback"
                    : "#access-denied"
                    }`}
                >
                  <HexagonCell type="sub" category="monitor" label="Surveys" icon="fa fa-commenting-o" activeCategory={activeCategory} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingHexagon;
